export type BrowserPermState = "unknown" | "prompt" | "granted" | "denied";

export type MediaErrorKind = "denied" | "missing" | "busy" | "other";

export const isAppleMobile = (
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
  touchPoints: number = typeof navigator !== "undefined" ? navigator.maxTouchPoints || 0 : 0
): boolean => /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);

export const isInAppBrowser = (ua: string = typeof navigator !== "undefined" ? navigator.userAgent : ""): boolean =>
  /WhatsApp|FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|Snapchat|TikTok|BytedanceWebview|GSA\/|Twitter|LinkedInApp|Discord|Telegram|Slack|Gmail/.test(
    ua
  );

/** Real iOS Safari exposes window.safari. Many in-app WebViews do not, even when the UA looks like Safari. */
export const isEmbeddedIosBrowser = (
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
  hasSafariGlobal?: boolean
): boolean => {
  if (!isAppleMobile(ua)) return false;
  if (/CriOS|FxiOS|EdgiOS/.test(ua)) return false;
  if (isInAppBrowser(ua)) return true;
  if (hasSafariGlobal == null) return false;
  return !hasSafariGlobal;
};

export const geoPermissionQueryUnreliable = (
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
  hasSafariGlobal?: boolean
): boolean => isAppleMobile(ua) || isInAppBrowser(ua) || isEmbeddedIosBrowser(ua, hasSafariGlobal);

export const queryBrowserPermission = async (name: string): Promise<BrowserPermState> => {
  try {
    const perms = navigator.permissions;
    if (!perms?.query) return "unknown";
    const status = await perms.query({ name } as PermissionDescriptor);
    if (status.state === "granted" || status.state === "denied" || status.state === "prompt") {
      return status.state;
    }
    return "unknown";
  } catch {
    return "unknown";
  }
};

export const mediaErrorKind = (err: unknown): MediaErrorKind => {
  const name = err && typeof err === "object" && "name" in err ? String((err as { name?: string }).name) : "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
    return "denied";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError") {
    return "missing";
  }
  if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") {
    return "busy";
  }
  return "other";
};

export const geoErrorKind = (err: unknown): MediaErrorKind => {
  const code = err && typeof err === "object" && "code" in err ? Number((err as { code?: number }).code) : 0;
  if (code === 1) return "denied";
  if (code === 2) return "missing";
  if (code === 3) return "busy";
  return mediaErrorKind(err);
};

export const permissionSettingsHelp = (
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
  hasSafariGlobal?: boolean
): string => {
  if (isInAppBrowser(ua) || isEmbeddedIosBrowser(ua, hasSafariGlobal)) {
    return "Open this link in Safari or Chrome. Location does not work inside WhatsApp, Instagram, or other in-app browsers.";
  }
  if (isAppleMobile(ua)) {
    return "Allow location first, then tap a square for the camera. Safari can show only one permission at a time. Safari’s Location Ask/Allow switch is not this site — also leave Settings → Privacy & Security → Location Services → Safari Websites on While Using.";
  }
  return "If no prompt appears, tap the lock or camera icon in the address bar, allow Camera, Microphone, and Location, then tap Try again here.";
};

export const permissionBlockedHelp = (
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
  hasSafariGlobal?: boolean
): string => {
  if (isInAppBrowser(ua) || isEmbeddedIosBrowser(ua, hasSafariGlobal)) {
    return "This in-app browser is blocking location. Tap Open in Safari (or Chrome) from the share menu, then tap Allow location on this page.";
  }
  if (isAppleMobile(ua)) {
    return "Safari Ask/Allow for all websites is not enough. Turn on Settings → Privacy & Security → Location Services → Safari Websites → While Using. Then in Safari tap aA → Website Settings → Location → Allow. Return here and tap Try location again.";
  }
  return "You denied access. The browser will not ask again until you change site settings. Tap the lock or camera icon in the address bar, set Camera, Microphone, and Location to Allow, then tap Try again.";
};

export const permissionActionLabel = (
  asking: boolean,
  blocked: boolean,
  idleLabel: string,
  retryLabel: string
): string => {
  if (asking) return "Asking…";
  return blocked ? retryLabel : idleLabel;
};

export type CaptureGestureNeed = "location" | "camera" | "microphone";

/** iOS Safari shows one prompt per tap. Location must win that tap or it is silently denied. */
export const nextCaptureGestureNeed = (state: {
  geoReady: boolean;
  cameraReady: boolean;
  micReady: boolean;
  allowsVideo: boolean;
}): CaptureGestureNeed | null => {
  if (!state.geoReady) return "location";
  if (!state.cameraReady) return "camera";
  if (state.allowsVideo && !state.micReady) return "microphone";
  return null;
};

export const watchBrowserPermission = (
  name: string,
  onChange: (state: BrowserPermState) => void
): (() => void) => {
  let status: PermissionStatus | null = null;
  const emit = () => {
    const state = status?.state;
    if (state === "granted" || state === "denied" || state === "prompt") {
      onChange(state);
      return;
    }
    onChange("unknown");
  };
  if (!navigator.permissions?.query) {
    return () => undefined;
  }
  navigator.permissions
    .query({ name } as PermissionDescriptor)
    .then((next) => {
      status = next;
      emit();
      next.addEventListener("change", emit);
    })
    .catch(() => undefined);
  return () => {
    status?.removeEventListener("change", emit);
  };
};

export const mediaErrorMessage = (kind: MediaErrorKind, device: "camera" | "microphone"): string => {
  if (kind === "denied") {
    return `Allow the ${device} in the browser prompt, or in site settings if it was blocked.`;
  }
  if (kind === "missing") {
    return `No ${device} was found on this phone.`;
  }
  if (kind === "busy") {
    return `The ${device} is in use by another app. Close it and tap Allow again.`;
  }
  return `Could not open the ${device}. Tap Allow and accept the browser prompt.`;
};

export const geoErrorMessage = (kind: MediaErrorKind): string => {
  if (kind === "denied") {
    return "Allow location in the browser prompt, or in site settings if it was blocked.";
  }
  if (kind === "missing") {
    return "Location is not available. Turn on Location Services and tap Allow location.";
  }
  if (kind === "busy") {
    return "Location is taking too long. Move to an open area and tap Allow location.";
  }
  return "Turn on location so this take can be geotagged.";
};

const VIDEO_CONSTRAINTS: Array<boolean | MediaTrackConstraints> = [
  { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
  { facingMode: { ideal: "environment" } },
  true,
];

export const requestCameraStream = async (): Promise<MediaStream> => {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw Object.assign(new Error("This phone cannot open the camera in the browser."), { name: "NotFoundError" });
  }
  let lastErr: unknown;
  for (const video of VIDEO_CONSTRAINTS) {
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: false, video });
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr;
};

export const requestMicrophoneStream = async (): Promise<MediaStream> => {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw Object.assign(new Error("This phone cannot open the microphone in the browser."), { name: "NotFoundError" });
  }
  return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
};

export const mergeAudioIntoStream = (videoStream: MediaStream, audioStream: MediaStream): void => {
  audioStream.getAudioTracks().forEach((track) => {
    if (!videoStream.getAudioTracks().some((existing) => existing.id === track.id)) {
      videoStream.addTrack(track);
    }
  });
};
